import { useEffect, useState } from 'react'
import { DotMatrix, Banner, Card, CodeBlock, PageHeader } from '@clickguard/design-system'
import { useStores } from '../../../../../hooks'
import { PageTitle } from '../../../../../utils/page-title'
import {
	conversionCode,
	conversionCodeWithMessage,
	conversionCodeWithMessageAndValue,
	conversionScript
} from '../../../../../config/tracking-code'
import { PiWarningDiamondFill } from 'react-icons/pi'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'
import { useNavigate } from 'react-router-dom'
import { useTrackPostHogEvent } from '../../../../../utils/posthog'
import { AdAccountLinks, ShopifyLinkStatusDTO } from 'services/user/ad-account-links'
import { ShopifyConnectSettings } from 'components/shopify-install'

const cardShell =
	'w-full !rounded-xl !border-t-traffic !shadow-[0px_4px_8px_0px_rgba(16,24,40,0.03),0px_1px_2px_0px_rgba(16,24,40,0.02)]'

export const ConversionTrackingSection = () => {
	const {
		assetStore: { asset, getDomainForScripts },
		accountStore: { account, hasFeature }
	} = useStores()
	const navigate = useNavigate()
	const trackPostHogEvent = useTrackPostHogEvent()

	const hasAccessToServerSideTracking = hasFeature('server_side_tracking')
	const hasTrackingCodeInstalled = asset?.tc_installed
	const domainForScripts =
		asset && hasAccessToServerSideTracking
			? getDomainForScripts(asset)
			: undefined

	// Is conversion tracking handled by Shopify for this asset? undefined = still checking.
	const [shopifyLink, setShopifyLink] = useState<
		ShopifyLinkStatusDTO | null | undefined
	>(undefined)

	useEffect(() => {
		let cancelled = false
		if (account?.id && asset?.id) {
			AdAccountLinks.getShopifyLink(account.id, asset.id)
				.then((link) => {
					if (!cancelled) setShopifyLink(link)
				})
				.catch(() => {
					if (!cancelled) setShopifyLink(null)
				})
		}
		return () => {
			cancelled = true
		}
	}, [account?.id, asset?.id])

	const handleTrackingCodeClick = () => {
		if (account?.id && asset?.id) {
			navigate(
				`/settings/workspace/${account.id}/asset/${asset.id}/tracking-code`
			)
		}
	}

	const ready = Boolean(account?.id && asset?.id)
	const landingCode = ready ? conversionScript(domainForScripts) : '< code sample >'
	const customEventsCode = ready
		? `${conversionCode()}\n${conversionCodeWithMessage()}\n${conversionCodeWithMessageAndValue()}`
		: '< code sample >'

	return (
		<div className="w-full">
			<PageTitle pageName="Conversion tracking" />
			<PageHeader
				layout="stacked"
				title="Conversion tracking"
				subtitle={
					<span className="inline-flex items-center font-normal text-t-checkbox-unactive">
						Block visitors who repeatedly click your ads without converting, or
						block them after they’ve already converted.
					</span>
				}
			/>
			<div className="pt-4">
				{shopifyLink === undefined ? (
					<Card padding="none" className={cardShell}>
						<div className="flex items-center gap-3 p-4">
							<DotMatrix size="sm" tone="inherit" className="text-primary-500" />
							<span className="text-[14px] leading-[18px] text-[color:var(--text-secondary)]">
								Checking your conversion tracking…
							</span>
						</div>
					</Card>
				) : shopifyLink ? (
					<Card padding="none" className={cardShell}>
						<div className="p-4">
							<ShopifyConnectSettings accountId={account!.id} assetId={asset!.id} initialLink={shopifyLink} />
						</div>
					</Card>
				) : (
					<Card padding="none" className={cardShell}>
						<div className="flex flex-col gap-6 p-4">
							{!hasTrackingCodeInstalled && (
								<Banner
									tone="warning"
									leading={<PiWarningDiamondFill className="h-5 w-5" />}
									title={
										<span className="text-[14px] leading-[18px]">
											First, install{' '}
											<button
												type="button"
												className="cursor-pointer font-semibold text-brand hover:underline"
												onClick={handleTrackingCodeClick}
											>
												ClickGuard&apos;s tracking code
											</button>{' '}
											to enable Conversion Tracking.
										</span>
									}
								/>
							)}

							<section className="flex flex-col gap-2">
								<h2 className="text-[16px] leading-[22px] font-medium text-t-base-blue">
									Landing page conversion
								</h2>
								<p className="text-[14px] leading-[18px] text-[color:var(--text-secondary)]">
									When loading a specific page should be reported as a conversion,
									like a &quot;Thank You&quot; page. Put the following code at the
									bottom of the page body.
								</p>
								<CodeBlock code={landingCode} />
							</section>

							<section className="flex flex-col gap-2">
								<h2 className="text-[16px] leading-[22px] font-medium text-t-base-blue">
									Custom events
								</h2>
								<p className="text-[14px] leading-[18px] text-[color:var(--text-secondary)]">
									When triggering a conversion on other events, like clicking on a
									button or submitting a form, you need to execute the following
									code when those events happen.
								</p>
								<CodeBlock code={customEventsCode} />
							</section>

							<p className="text-[14px] leading-[18px] text-[color:var(--text-secondary)]">
								Visit the{' '}
								<a
									className="font-semibold text-brand hover:underline"
									href="https://docs.clickguard.com/en/articles/21548-how-to-track-conversions-in-clickguard-with"
									target="_blank"
									rel="noreferrer"
									onClick={() => {
										trackPostHogEvent(
											PostHogEvent.LINK_KNOWLEDGE_BASE,
											PostHogPath.SETTINGS_TC,
											null
										)
									}}
								>
									Knowledge base
								</a>{' '}
								for additional help.
							</p>
						</div>
					</Card>
				)}
			</div>
		</div>
	)
}
